/*
  InStyle — a product card keeps changing photos while the mouse rests on it.
  ------------------------------------------------------------------
  Naman, 2026-10-02: "when a person is hovering over a product in any page it
  should keep changing pictures; currently it changes one, but at certain time
  it should keep changing pics."

  The card's first hover is unchanged: the CSS cross-fades its second photo in
  (snippets/instyle-card.liquid, assets/instyle-collection.css). From there this
  script steps on every DWELL ms through the product's first PHOTOS images
  (the new set: main, another angle, detail, in a room), 2 → 3 → 4 → 1 → 2 …,
  until the pointer leaves; then the card shows its first photo again.

  How a step cross-fades with one extra layer: a third <img> (.instyle-card__img3)
  sits above the hover photo. The next photo loads into it and fades in; once
  the fade is over, the hover photo underneath takes the same picture and the
  third layer is hidden without a transition, so nothing visible changes. The
  next step repeats that. The photo list is the card's own
  `data-instyle-gallery` (built for assets/instyle-card-gallery.js).

  Only where the hover swap is live: a hover-capable pointer, at least 990px
  wide (the same gate as the CSS), a mouse pointer event, and not
  prefers-reduced-motion (there the card keeps its single swap). Events are
  delegated from the document, so cards re-rendered by filters, sorting or a
  colour-dot swap need no re-binding. Loaded site-wide from layout/theme.liquid.
*/
(function () {
  'use strict';
  if (window.InStyleCardCycle) return;
  window.InStyleCardCycle = true;

  var DWELL = 1200; // ms each photo stays before the next fades in
  var FADE = 320; // a little longer than the 300ms CSS transition
  var PHOTOS = 4; // cycle through the first four photos only

  var gate = window.matchMedia
    ? window.matchMedia('(hover: hover) and (min-width: 990px)')
    : null;
  var reduced = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
  if (!gate) return;

  var active = null; // { card, timer, list, next, img2, img3, src2, srcset2 }

  function photos(card) {
    var img = card.querySelector('img[data-instyle-gallery]');
    if (!img) return [];
    return img
      .getAttribute('data-instyle-gallery')
      .split('|')
      .filter(Boolean)
      .slice(0, PHOTOS);
  }

  function layer(card, img2) {
    var img3 = card.querySelector('img.instyle-card__img3');
    if (!img3) {
      img3 = document.createElement('img');
      img3.className = 'instyle-card__img3';
      img3.alt = '';
      img3.setAttribute('aria-hidden', 'true');
      img3.decoding = 'async';
      img2.parentNode.insertBefore(img3, img2.nextSibling);
    }
    return img3;
  }

  function step() {
    var a = active;
    if (!a) return;
    if (!a.card.isConnected) return stop();
    var url = a.list[a.next];
    a.next = (a.next + 1) % a.list.length;
    var loader = new Image();
    loader.onload = loader.onerror = function () {
      if (active !== a) return;
      a.img3.classList.remove('is-reset');
      a.img3.src = url;
      a.img3.classList.add('is-on');
      a.timer = setTimeout(function () {
        if (active !== a) return;
        // The hover photo takes the same picture underneath, then the top layer
        // drops out without a fade: nothing visible changes.
        a.img2.removeAttribute('srcset');
        a.img2.src = url;
        a.img3.classList.add('is-reset');
        a.img3.classList.remove('is-on');
        a.timer = setTimeout(step, DWELL);
      }, FADE);
    };
    loader.src = url;
  }

  function start(card) {
    if (!gate.matches || (reduced && reduced.matches)) return;
    var img2 = card.querySelector('img.instyle-card__img2');
    var list = photos(card);
    if (!img2 || list.length < 3) return; // two photos: the CSS swap is the whole story
    stop();
    active = {
      card: card,
      list: list,
      next: 2, // photo 2 is already showing (the CSS hover)
      img2: img2,
      img3: layer(card, img2),
      src2: img2.getAttribute('src'),
      srcset2: img2.getAttribute('srcset'),
      timer: null
    };
    active.timer = setTimeout(step, DWELL);
  }

  function stop() {
    var a = active;
    if (!a) return;
    active = null;
    clearTimeout(a.timer);
    a.img3.classList.add('is-reset');
    a.img3.classList.remove('is-on');
    // Put the hover photo back to photo 2 after the CSS has faded it out, so the
    // next hover starts where it always did.
    setTimeout(function () {
      if (active && active.card === a.card) return;
      if (a.srcset2) a.img2.setAttribute('srcset', a.srcset2);
      if (a.src2) a.img2.setAttribute('src', a.src2);
    }, 400);
  }

  document.addEventListener('pointerover', function (e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    var card = e.target.closest && e.target.closest('.instyle-card--swap');
    if (!card || (active && active.card === card)) return;
    start(card);
  });

  document.addEventListener('pointerout', function (e) {
    if (!active) return;
    var to = e.relatedTarget;
    if (to && active.card.contains(to)) return;
    stop();
  });

  // A hidden tab or a page restored from the back-forward cache starts clean.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stop();
  });
  window.addEventListener('pagehide', stop);
})();
